import { ApiResult, HttpClient } from "../http.js";
import { PagedProducts, ProductDto } from "../types/products.js";

export interface CreateProductPayload {
  name?: string | null;
  comment?: string | null;
  baseAmountValue: number;
  quantity: number;
  accountNumber: number;
  unit: string;
  isPriceLibraryItem?: boolean;
  priceLibraryCategoryId?: string | null;
  priceLibrarySortOrder?: number | null;
  priceLibraryUnit?: string | null;
  hoursPerUnit?: number | null;
  materialsPerUnit?: number | null;
  equipmentPerUnit?: number | null;
  subcontractorPerUnit?: number | null;
  id?: string;
}

export interface UpdateProductPayload {
  name?: string | null;
  comment?: string | null;
  baseAmountValue: number;
  quantity: number;
  accountNumber: number;
  unit: string;
  isPriceLibraryItem?: boolean;
  priceLibraryCategoryId?: string | null;
  priceLibrarySortOrder?: number | null;
  priceLibraryUnit?: string | null;
  hoursPerUnit?: number | null;
  materialsPerUnit?: number | null;
  equipmentPerUnit?: number | null;
  subcontractorPerUnit?: number | null;
}

export interface ListProductsQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  isPriceLibraryItem?: boolean;
  /** Only price-library products in this category (`priceLibraryCategoryId`). */
  priceLibraryCategoryId?: string;
}

export class ProductsApi {
  constructor(private readonly http: HttpClient) {}

  list(query: ListProductsQuery = {}): Promise<ApiResult<PagedProducts>> {
    return this.http.get<PagedProducts>("/api/products", { query: { ...query } });
  }

  get(id: string): Promise<ApiResult<ProductDto>> {
    return this.http.get<ProductDto>(`/api/products/${encodeURIComponent(id)}`);
  }

  create(payload: CreateProductPayload): Promise<ApiResult<string>> {
    return this.http.post<string>("/api/products", { body: payload });
  }

  update(id: string, payload: UpdateProductPayload): Promise<ApiResult<ProductDto>> {
    return this.http.put<ProductDto>(`/api/products/${encodeURIComponent(id)}`, { body: payload });
  }

  /** Soft delete — the row survives with `deletedAt` stamped. */
  remove(id: string): Promise<ApiResult<boolean>> {
    return this.http.delete<boolean>(`/api/products/${encodeURIComponent(id)}`);
  }

  /**
   * Permanent delete — the row is removed. Also reaches already soft-deleted
   * products, so `remove` followed by `purge` works.
   */
  purge(id: string): Promise<ApiResult<boolean>> {
    return this.http.delete<boolean>(`/api/products/${encodeURIComponent(id)}/purge`);
  }
}
